document.addEventListener('DOMContentLoaded', () => {
  const bodyWidth = document.body.clientWidth;
  const DESKTOP_BREAKPOINT = 1367;

  // —— Helpers ——

  /**
   * Throttles a function to run at most once per animation frame.
   * @param {() => void} fn
   * @returns {() => void}
   */
  function throttleRAF(fn) {
    let rafId = null;
    return () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        fn();
        rafId = null;
      });
    };
  }

  /**
   * Toggles a class on elements when they enter the viewport.
   * @param {string} selector
   * @param {{ offset?: number, activeClass?: string, secondOffset?: number, secondClass?: string }} options
   */
  function setActiveWhenInView(selector, options = {}) {
    const offset = options.offset ?? 100;
    const activeClass = options.activeClass ?? 'active';
    const secondOffset = options.secondOffset;
    const secondClass = options.secondClass;
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const windowHeight = window.innerHeight;

    for (const el of elements) {
      const top = el.getBoundingClientRect().top;
      const inView = top < windowHeight - offset;

      el.classList.toggle(activeClass, inView);

      if (secondOffset !== undefined && secondClass) {
        const inSecondZone = top < windowHeight - secondOffset;
        el.classList.toggle(secondClass, inView && inSecondZone);
      }
    }
  }

  /**
   * Runs setActiveWhenInView only if at least one element matches the selector.
   * @param {string} selector
   * @param {{ offset?: number, activeClass?: string, secondOffset?: number, secondClass?: string }} options
   */
  function setActiveWhenInViewIfExists(selector, options = {}) {
    if (!document.querySelector(selector)) return;
    setActiveWhenInView(selector, options);
  }

  /**
   * Single scroll handler: runs all viewport-based animations once per frame.
   * Each block runs only when its selector exists on the page.
   */
  const runScrollAnimations = throttleRAF(() => {
    const windowHeight = window.innerHeight;

    // Cuban rounds sections (desktop only)
    if (bodyWidth > DESKTOP_BREAKPOINT && document.querySelector('.cuban-rounds .cuban-section')) {
      setActiveWhenInView('.cuban-rounds .cuban-section', { offset: 100 });
    }

    // Cuban heading: product sections drive corresponding .section-sold
    const productSections = document.querySelectorAll('.cuban-rounds .section-product');
    if (productSections.length > 0) {
      const soldSections = document.querySelectorAll('.cuban-rounds .section-sold');
      const visible = 150;
      const visibleSecond = 400;

      for (let i = 0; i < productSections.length; i++) {
        const sold = soldSections[i];
        if (!sold) continue;

        const top = productSections[i].getBoundingClientRect().top;
        const inView = top < windowHeight - visible;

        sold.classList.toggle('headingactive', inView);
        sold.classList.toggle('headingactivesecond', inView && top < windowHeight - visibleSecond);
      }
    }

    // Generic in-view animations (only when selector exists)
    setActiveWhenInViewIfExists('.animation', { offset: 100 });
    setActiveWhenInViewIfExists('.crafted_section.desktop_view', { offset: 20 });
    setActiveWhenInViewIfExists('.fresssealed_section.desktop_view', { offset: 100 });
    setActiveWhenInViewIfExists('.evolution_section.flavor_section.desktop_view', { offset: 20 });
    setActiveWhenInViewIfExists('.pre_roll_section.desktop_view', { offset: 20 });
    setActiveWhenInViewIfExists('.tobacco_free_section.desktop_view', { offset: 100 });
    setActiveWhenInViewIfExists('.tobacco_free_section.mobile_view', { offset: 100 });
    setActiveWhenInViewIfExists('.pouchespersec_cones.desktop_view', { offset: 100 });

    // Delayed class toggles (only when element exists)
    const sealedSection = document.querySelector('.fresssealed_section.mobile_view');
    if (sealedSection) {
      if (sealedSection.classList.contains('active')) {
        setTimeout(() => sealedSection.classList.add('newactive'), 5000);
      } else {
        sealedSection.classList.remove('newactive');
      }
    }

    if (document.querySelector('.evolution_section.flavor_section.desktop_view')) {
      const flavorSections = document.querySelectorAll('.evolution_section.flavor_section.desktop_view');
      setTimeout(() => {
        const wh = window.innerHeight;
        for (const el of flavorSections) {
          el.classList.toggle('newactivecustom', el.getBoundingClientRect().top < wh - 100);
        }
      }, 4000);
    }

    const packElement = document.querySelector('.comparisonSection.desktop_view > .pack');
    if (packElement) {
      if (packElement.classList.contains('active')) {
        setTimeout(() => packElement.classList.add('trigger'), 7000);
      } else {
        packElement.classList.remove('trigger');
      }
    }
  });

  window.addEventListener('scroll', runScrollAnimations, { passive: true });
  runScrollAnimations(); // run once on load

  // Set --cigar-heading-height on .cigar_heading_animation_cover (load + resize)
  function setCigarHeadingHeight() {
    const covers = document.querySelectorAll('.cigar_heading_animation_cover');
    for (const el of covers) {
      el.style.setProperty('--cigar-heading-height', `-${el.offsetHeight}px`);
    }
  }
  setCigarHeadingHeight();
  window.addEventListener('resize', throttleRAF(setCigarHeadingHeight));
});

// —— Safari detection (no console output) ——
(function () {
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    /iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(navigator.userAgent) ||
    (navigator.vendor?.indexOf('Apple') > -1 &&
      navigator.userAgent &&
      !navigator.userAgent.match('CriOS') &&
      !navigator.userAgent.match('FxiOS'));

  if (isSafari) {
    document.documentElement.classList.add('is-safari');
    document.querySelector('.filterd-section.details-section')?.classList.add('safari-browser');
    document.querySelector('.djarum_blackdistinct_section .six_content_img_classic')?.classList.add('safari-browser');
  }
})();

// Optimized hash scrolling handler
(function() {
  'use strict';
  
  // Reusable scroll function
  function scrollToElement(element, delay = 0) {
    if (!element) return;
    
    const scroll = () => {
      requestAnimationFrame(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    };
    
    delay > 0 ? setTimeout(scroll, delay) : scroll();
  }
  
  // Extract hash from URL
  function getHashFromUrl(url) {
    const hashIndex = url.indexOf('#');
    return hashIndex !== -1 ? url.substring(hashIndex + 1) : null;
  }
  
  // Check if URL is same page
  function isSamePage(href) {
    if (!href || href.startsWith('#')) return true;
    if (href.startsWith('http://') || href.startsWith('https://')) {
      try {
        return new URL(href).pathname === window.location.pathname;
      } catch {
        return false;
      }
    }
    // Relative URL - check if pathname matches
    const link = document.createElement('a');
    link.href = href;
    return link.pathname === window.location.pathname || 
            link.pathname === '' || 
            link.pathname === '/';
  }
  
  // Close menu drawer helper
  function closeMenuDrawer() {
    // Click the menu toggle button to close the drawer (same as clicking the close icon)
    const menuToggle = document.querySelector('.header__icon--menu');
    if (menuToggle) {
      menuToggle.click();
    }
  }
  
  // Handle hash scroll on click
  function handleHashClick(e) {
    const link = e.target.closest('.mega-menu__list .mega-menu__link, .menu-drawer__menu-item');
    if (!link) return;
    
    const href = link.getAttribute('href');
    if (!href) return;
    
    const targetId = getHashFromUrl(href);
    if (!targetId) return;
    
    const isSame = isSamePage(href);
    const isMenuDrawerItem = link.classList.contains('menu-drawer__menu-item');
    
    if (isSame) {
      e.preventDefault();
      scrollToElement(document.getElementById(targetId));
      
      // Close menu drawer on mobile when clicking menu-drawer__menu-item
      if (isMenuDrawerItem) {
        closeMenuDrawer();
      }
    }
    // For different pages, let browser navigate - hash will be handled on load
  }
  
  // Handle hash on page load
  function handleHashOnLoad() {
    const hash = window.location.hash;
    if (!hash) return;
    
    const targetId = hash.substring(1);
    scrollToElement(document.getElementById(targetId), 100);
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function initHashScroll() {
      // Use event delegation for better performance
      document.addEventListener('click', handleHashClick, true);
      handleHashOnLoad();
    });
  } else {
    // DOM already loaded
    document.addEventListener('click', handleHashClick, true);
    handleHashOnLoad();
  }
})();